import store, { persistor } from '../app/store';
import { persistStore } from 'redux-persist';
import { Provider } from 'react-redux';
import { PersistGate } from 'redux-persist/integration/react';
import { useIsomorphicLayoutEffect } from '../../commons/hooks';
import ThemeProvider from '../styles/v5';
// 3rd Party
import Layout from '../../components/layout/Layout';
import { config } from '../../config';
// CSS
import '../styles/globals.scss';
import 'react-toastify/dist/ReactToastify.css';
import Helmet from './Helmet';
import SentrySetup from '../../components/Sentry';
import ThirdPartyScripts from '../../commons/components/scripts';
import { injectRiskified, isAppleDevice } from '../../commons/utils';
import { Head } from './../../components/global';
import { useEffect, useState } from 'react';
import { FBPageView } from '../../commons/helpers/Analytics/fbPixel';
import ErrorBoundary from '../../components/ErrorBoundry';
import { vwoHelper } from '../../commons/helpers/Analytics/vwo/vwoHelper';
import { cookie, serialize, parse } from 'cookie';
import { v4 as uuidv4 } from 'uuid';
import initVWOSdk from '../../commons/helpers/Analytics/vwo/vwoHelper';
import { createHmac } from 'crypto';

function MyApp({ Component, pageProps, props }) {
  useEffect(() => {
    const handleChatBubbleOpen = () => {
      const userId = parse(document?.cookie || '').vwo_uid;
      if (userId && config?.scripts?.vwo && vwoHelper.vwoClientInstance) {
        const options = {
          eventProperties: {
            pagePath: window.location.pathname,
          },
        };
        try {
          vwoHelper.vwoClientInstance.track(
            config.vwo.campaignKey,
            userId,
            'chatBotUserMessage',
            options
          );
        } catch (err) {
          console.error('Error tracking VWO event:', err);
        }
      } else {
        console.warn(
          'VWO tracking skipped: Missing userId, VWO config, or client instance',
          {
            userId,
            vwoConfig: config?.scripts?.vwo,
            vwoClientInstance: !!vwoHelper.vwoClientInstance,
          }
        );
      }
    };

    // Check if Chatbase SDK is loaded
    if (
      window.chatbase &&
      typeof window.chatbase.addEventListener === 'function'
    ) {
      window.chatbase.addEventListener('user-message', handleChatBubbleOpen);
    } else {
      // Queue the command if SDK isn't loaded yet
      window.chatbase =
        window.chatbase ||
        function () {
          (window.chatbase.q = window.chatbase.q || []).push(arguments);
        };
      window.chatbase('addEventListener', 'user-message', handleChatBubbleOpen);
    }

    return () => {
      if (
        window.chatbase &&
        typeof window.chatbase.removeEventListener === 'function'
      ) {
        window.chatbase.removeEventListener(
          'user-message',
          handleChatBubbleOpen
        );
      }
    };
  }, []);

  // Centralized Chatbase user configuration
  const initializeChatbaseConfig = () => ({
    user_id: '',
    user_hash: '',
    user_metadata: { name: '', email: '' },
  });

  let chatBaseUserConfig = initializeChatbaseConfig();

  // Validate metadata length (max 1000 characters)
  const validateMetadata = (metadata) => {
    const metadataString = JSON.stringify(metadata);
    if (metadataString.length > 1000) {
      console.warn('Chatbase metadata exceeds 1000 characters, truncating.');
      return {
        name: metadata.name ? metadata.name.substring(0, 200) : '',
        email: metadata.email ? metadata.email.substring(0, 200) : '',
      };
    }
    return metadata;
  };

  // Set chatbase_user_id cookie with explicit options
  const setChatbaseCookie = (userId) => {
    try {
      const cookieString = serialize('chatbase_user_id', userId, {
        httpOnly: false,
        maxAge: 86400 * 15, // 15 days
        path: '/',
        sameSite: 'lax', // Ensure cookie is sent in cross-site requests
        secure: process.env.NODE_ENV === 'production', // Secure in production
      });
      document.cookie = cookieString;
    } catch (err) {
      console.error('Error setting chatbase cookie:', err);
    }
  };

  // Generate user hash
  const generateUserHash = (userId) => {
    try {
      const secretKey = config.chatBase.secretkey;
      if (!secretKey) {
        throw new Error('NEXT_PUBLIC_CHATBASE_SECRET_KEY is not defined');
      }
      const hash = createHmac('sha256', secretKey).update(userId).digest('hex');
      return hash;
    } catch (err) {
      console.error('Error generating user_hash:', err);
      return null;
    }
  };

  // Call Chatbase identify method
  const identifyChatbaseUser = (userConfig) => {
    try {
      window.chatbaseUserConfig = userConfig;
      if (!window.chatbase) {
        window.chatbase = (...args) => {
          window.chatbase.q = window.chatbase.q || [];
          window.chatbase.q.push(args);
        };
        window.chatbase = new Proxy(window.chatbase, {
          get(target, prop) {
            if (prop === 'q') return target.q;
            return (...args) => target(prop, ...args);
          },
        });
      }
      const identifyPayload = {
        user_id: userConfig.user_id,
        user_hash: userConfig.user_hash,
        user_metadata: validateMetadata(userConfig.user_metadata),
      };
      if (window.chatbase('getState') === 'initialized') {
        window.chatbase('identify', identifyPayload);
      } else {
        window.chatbase('identify', identifyPayload);
      }
    } catch (err) {
      console.error('Error identifying Chatbase user:', err);
    }
  };

  useEffect(() => {
    const manageUserIdentity = () => {
      try {
        const customerInfo = store.getState().user.customerInfo;
        const cookies = parse(document.cookie);
        let userId = cookies.chatbase_user_id;

        if (customerInfo?.id) {
          // Logged-in user
          userId = String(customerInfo.id);
          const userHash = generateUserHash(userId);
          if (!userHash) {
            console.error('Failed to generate user_hash for logged-in user');
            return;
          }

          chatBaseUserConfig = {
            user_id: userId,
            user_hash: userHash,
            user_metadata: validateMetadata({
              name: customerInfo.firstname || 'AnonymousUser',
              email: customerInfo.email || '',
            }),
          };
        } else {
          // Anonymous user or logout
          if (!userId) {
            // No cookie exists, create new anonymous ID
            userId = uuidv4();
            console.debug(
              'No cookie found, generated new anonymous ID:',
              userId
            );
          } else if (
            chatBaseUserConfig.user_id &&
            chatBaseUserConfig.user_metadata.name !== 'AnonymousUser'
          ) {
            // After logout, reset to new anonymous ID
            userId = uuidv4();
          } else {
            console.debug('Reusing existing anonymous ID:', userId);
          }

          const userHash = generateUserHash(userId);
          if (!userHash) {
            console.error('Failed to generate user_hash for anonymous user');
            return;
          }

          chatBaseUserConfig = {
            user_id: userId,
            user_hash: userHash,
            user_metadata: { name: 'AnonymousUser', email: '' },
          };
        }

        // Only set cookie if userId has changed or is new
        if (userId !== cookies.chatbase_user_id) {
          setChatbaseCookie(userId);
        } else {
          console.debug('Cookie unchanged, skipping set');
        }

        identifyChatbaseUser(chatBaseUserConfig);
      } catch (err) {
        console.error('Error managing user identity:', err);
      }
    };

    // Run immediately
    manageUserIdentity();

    // Subscribe to Redux store changes
    const unsubscribe = store.subscribe(() => {
      const newCustomerInfo = store.getState().user.customerInfo;
      const currentUserId = chatBaseUserConfig.user_id;
      if (
        (newCustomerInfo?.id && newCustomerInfo.id !== currentUserId) ||
        (!newCustomerInfo?.id &&
          currentUserId &&
          chatBaseUserConfig.user_metadata.name !== 'AnonymousUser')
      ) {
        manageUserIdentity();
      }
    });

    return () => unsubscribe();
  }, []);

  // Existing useIsomorphicLayoutEffect and other useEffect hooks remain unchanged
  useIsomorphicLayoutEffect(() => {
    injectRiskified();
    let purgeConfig = JSON.parse(localStorage?.getItem('__rdx_prst_prg'));
    if (
      config.redux.persist.purge &&
      (!purgeConfig?.version ||
        purgeConfig?.version !== config.redux.persist.version)
    ) {
      persistor.pause();
      persistor
        .flush()
        .then(() => persistor.purge().then(() => persistor.persist()));
      localStorage.setItem(
        '__rdx_prst_prg',
        JSON.stringify({
          version: config.redux.persist.version,
        })
      );
    }
  }, []);
  useEffect(() => {
    FBPageView();
    try {
      let bodySelector = document.querySelector('body');
      if (isAppleDevice()) {
        bodySelector.classList.remove('custom-scrollbar');
      } else bodySelector.classList.add('custom-scrollbar');
    } catch (err) {
      console.log(err);
    }
  }, []);
  try {
    //  VWO Settings
    let userId = props?.userId;

    if (userId && config?.scripts?.vwo) {
      let accountId = config.vwo.accountId;
      let sdkKey = config.vwo.sdkKey;
      const userData = {
        campaignKey: config.vwo.campaignKey,
        userId: userId,
        variationName: props.variationName,
      };
      let currentSettingsFile = {};
      vwoHelper
        .getSettingsFile(accountId, sdkKey)
        .then((latestSettingsFile) => {
          currentSettingsFile = latestSettingsFile;
          vwoHelper.currentSettingsFile = latestSettingsFile;
          vwoHelper.initVWOSdk(latestSettingsFile, userData, props);
        })
        .catch((err) => {
          console.error(
            'Something went wrong in fetching account settings.',
            err
          );
        });
    }
  } catch (err) {
    console.log('VWO Error:', err);
  }

  let updateProps = { ...pageProps, ...props };

  return config.sentry.enabled === true ? (
    <>
      <SentrySetup>
        <Provider store={store}>
          <ThemeProvider>
            <Head
              title={pageProps?.extraMeta?.title}
              description={pageProps?.extraMeta?.description}
              schemaData={pageProps?.extraMeta?.schemaData}
            />
            <Helmet />
            <Layout>
              <ErrorBoundary>
                <Component {...updateProps} />
              </ErrorBoundary>
            </Layout>
            <PersistGate loading={null} persistor={persistor} />
          </ThemeProvider>
        </Provider>
        <ThirdPartyScripts />
      </SentrySetup>
    </>
  ) : (
    <>
      <Provider store={store}>
        <ThemeProvider>
          <Head
            title={pageProps?.extraMeta?.title}
            description={pageProps?.extraMeta?.description}
            schemaData={pageProps?.extraMeta?.schemaData}
          />
          <Helmet />
          <Layout>
            <ErrorBoundary>
              <Component {...updateProps} />
            </ErrorBoundary>
          </Layout>
          <PersistGate loading={null} persistor={persistor} />
        </ThemeProvider>
      </Provider>
      <ThirdPartyScripts />
    </>
  );
}

export default MyApp;
MyApp.getInitialProps = async (context) => {
  let userId = null;
  let variationName = 'control-a';
  let userAgent = '';
  let userIP = '';
  try {
    userAgent = context?.ctx?.req?.headers['user-agent'] || '';
    userIP =
      context?.ctx?.req?.headers['x-real-ip'] ||
      context?.ctx?.req?.headers['x-forwarded-for'] ||
      context?.ctx?.req?.connection?.remoteAddress ||
      '';

    if (context?.ctx?.req?.cookies?.vwo_uid) {
      userId = context?.ctx?.req?.cookies?.vwo_uid;
    } else {
      userId = uuidv4();
      const setCookie = serialize('vwo_uid', userId, {
        httpOnly: false,
        maxAge: 86400,
        secure: false,
        path: '/',
      });
      context.ctx.res.setHeader('Set-Cookie', setCookie);
    }
    const vwoClientInstance = await initVWOSdk(config.vwo.sdkKey);
    let options = {
      userStorageData: vwoClientInstance.userStorageService,
      userAgent: userAgent,
      userIpAddress: userIP,
    };
    variationName = vwoClientInstance.activate(
      config.vwo.campaignKey,
      userId,
      options
    );
    const userData = {
      campaignKey: config.vwo.campaignKey,
      userId: userId,
      variationName: variationName,
    };
    const setUserStorageService =
      vwoClientInstance.userStorageService.set(userData);
  } catch (err) {
    console.log(err);
  }

  return {
    props: {
      userId,
      userAgent,
      userIP,
      variationName: variationName,
    },
  };
};
